import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { apiFetch, ApiError } from "@shared/lib/api";
import { Alert, Button, Card, Input, Spinner } from "@shared/components/ui";
import { Logo } from "@shared/components/Logo";
import type { PasswordResetValidateResponse, PasswordResetResetResponse } from "@shared/lib/types";

type PageState = "loading" | "valid" | "invalid" | "success";

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";

  const [pageState, setPageState] = useState<PageState>("loading");
  const [tokenError, setTokenError] = useState<string>("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [errors, setErrors] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setTokenError("No reset token provided");
      setPageState("invalid");
      return;
    }

    async function validateToken() {
      try {
        const { data } = await apiFetch<PasswordResetValidateResponse>(
          `/api/v1/auth/password/validate?token=${encodeURIComponent(token)}`
        );
        if (data.valid) {
          setPageState("valid");
        } else {
          setTokenError("Invalid or expired reset link");
          setPageState("invalid");
        }
      } catch (err) {
        const errorMessages = err instanceof ApiError ? err.errors : ["Invalid or expired reset link"];
        setTokenError(errorMessages[0] || "Invalid or expired reset link");
        setPageState("invalid");
      }
    }

    validateToken();
  }, [token]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrors([]);

    // Client-side validation
    const clientErrors: string[] = [];
    if (password.length < 8) {
      clientErrors.push("Password must be at least 8 characters");
    }
    if (password !== passwordConfirmation) {
      clientErrors.push("Passwords do not match");
    }
    if (clientErrors.length > 0) {
      setErrors(clientErrors);
      return;
    }

    setSubmitting(true);

    try {
      await apiFetch<PasswordResetResetResponse>("/api/v1/auth/password/reset", {
        method: "POST",
        body: {
          token,
          password,
          password_confirmation: passwordConfirmation,
        },
      });
      setPageState("success");
    } catch (err) {
      const errorMessages = err instanceof ApiError ? err.errors : [err instanceof Error ? err.message : "Unknown error"];
      setErrors(errorMessages.length > 0 ? errorMessages : ["Something went wrong"]);
    } finally {
      setSubmitting(false);
    }
  }

  // Loading state
  if (pageState === "loading") {
    return (
      <div className="min-h-screen bg-bg">
        <div className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center px-4 py-12">
          <Logo size="lg" className="mb-8" />
          <Card className="w-full">
            <div className="flex flex-col items-center gap-4 py-8">
              <Spinner />
              <p className="text-muted">Validating reset link...</p>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (pageState === "invalid") {
    return (
      <div className="min-h-screen bg-bg">
        <div className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center px-4 py-12">
          <Logo size="lg" className="mb-8" />
          <Card title="Invalid Reset Link" className="w-full">
            <div className="space-y-4">
              <Alert items={[tokenError]} />
              <p className="text-muted">
                This password reset link is invalid or has expired. Please request a new one.
              </p>
              <div className="flex gap-3">
                <Link to="/forgot-password">
                  <Button>Request new link</Button>
                </Link>
                <Link to="/login">
                  <Button variant="secondary">Back to sign in</Button>
                </Link>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // Success state
  if (pageState === "success") {
    return (
      <div className="min-h-screen bg-bg">
        <div className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center px-4 py-12">
          <Logo size="lg" className="mb-8" />
          <Card title="Password Reset Successful" className="w-full">
            <div className="space-y-4">
              <p className="text-muted">
                Your password has been reset successfully. You can now sign in with your new password.
              </p>
              <Button onClick={() => navigate("/login", { replace: true })}>
                Sign in
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // Valid token - show reset form
  return (
    <div className="min-h-screen bg-bg">
      <div className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center px-4 py-12">
        <Logo size="lg" className="mb-8" />
        <Card title="Reset your password" className="w-full">
          <p className="mb-4 text-muted">
            Enter your new password below.
          </p>
          <form className="space-y-4" onSubmit={onSubmit}>
            <Alert items={errors} />
            <Input
              label="New password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              autoFocus
            />
            <Input
              label="Confirm new password"
              type="password"
              value={passwordConfirmation}
              onChange={(e) => setPasswordConfirmation(e.target.value)}
              autoComplete="new-password"
            />
            <p className="text-xs text-muted">
              Password must be at least 8 characters.
            </p>
            <div className="flex items-center justify-between gap-3">
              <Button type="submit" disabled={submitting || !password || !passwordConfirmation}>
                {submitting ? "Resetting..." : "Reset password"}
              </Button>
              {submitting ? <Spinner /> : null}
            </div>
          </form>

          <div className="mt-4 text-sm text-muted">
            Remember your password?{" "}
            <Link className="font-medium text-foreground underline" to="/login">
              Sign in
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
